// Utility for encoding/decoding project IDs in public URLs
// This provides security through obscurity - making URLs harder to guess

const ENCODING_OFFSET = 89247; // Random offset to make IDs less predictable
const ENCODING_MULTIPLIER = 1871; // Prime number multiplier

/**
 * Encodes a numeric project ID into an obfuscated string for public URLs
 * @param projectId - The numeric project ID
 * @returns An encoded string suitable for URLs
 */
export function encodeProjectId(projectId: number): string {
  if (!projectId || isNaN(projectId)) {
    throw new Error('Invalid project ID provided for encoding');
  }
  
  // Apply mathematical transformation to obfuscate the ID
  const transformed = (projectId * ENCODING_MULTIPLIER) + ENCODING_OFFSET;
  
  // Convert to base36 string (0-9, a-z) for URL-friendly format
  return transformed.toString(36);
}

/**
 * Decodes an obfuscated string back to the original numeric project ID
 * @param encodedId - The encoded string from the URL
 * @returns The original numeric project ID
 */
export function decodeProjectId(encodedId: string): number {
  if (!encodedId || typeof encodedId !== 'string') {
    throw new Error('Invalid encoded ID provided for decoding');
  }
  
  try {
    // Parse from base36 back to number
    const transformed = parseInt(encodedId, 36);
    
    if (isNaN(transformed)) {
      throw new Error('Invalid encoded ID format');
    }
    
    // Reverse the mathematical transformation
    const originalId = (transformed - ENCODING_OFFSET) / ENCODING_MULTIPLIER;
    
    // Ensure we get a valid integer
    if (!Number.isInteger(originalId) || originalId <= 0) {
      throw new Error('Decoded ID is not a valid project ID');
    }
    
    return originalId;
  } catch (error) {
    throw new Error(`Failed to decode project ID: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Check if a string looks like an encoded project ID
 * @param value - The string to check
 * @returns True if it appears to be an encoded ID
 */
export function isEncodedProjectId(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  
  // Check if it's a valid base36 string
  return /^[0-9a-z]+$/i.test(value);
}

/**
 * Encodes a numeric activity ID into an obfuscated string for public URLs
 * Uses the same algorithm as project IDs for consistency
 * @param activityId - The numeric activity ID
 * @returns An encoded string suitable for URLs
 */
export function encodeActivityId(activityId: number): string {
  if (!activityId || isNaN(activityId)) {
    throw new Error('Invalid activity ID provided for encoding');
  }
  
  // Apply mathematical transformation to obfuscate the ID
  const transformed = (activityId * ENCODING_MULTIPLIER) + ENCODING_OFFSET;
  
  // Convert to base36 string (0-9, a-z) for URL-friendly format
  return transformed.toString(36);
}

/**
 * Decodes an obfuscated string back to the original numeric activity ID
 * Uses the same algorithm as project IDs for consistency
 * @param encodedId - The encoded string from the URL
 * @returns The original numeric activity ID
 */
export function decodeActivityId(encodedId: string): number {
  if (!encodedId || typeof encodedId !== 'string') {
    throw new Error('Invalid encoded ID provided for decoding');
  }
  
  try {
    // Parse from base36 back to number
    const transformed = parseInt(encodedId, 36);
    
    if (isNaN(transformed)) {
      throw new Error('Invalid encoded ID format');
    }
    
    // Reverse the mathematical transformation
    const originalId = (transformed - ENCODING_OFFSET) / ENCODING_MULTIPLIER;
    
    // Ensure we get a valid integer
    if (!Number.isInteger(originalId) || originalId <= 0) {
      throw new Error('Decoded ID is not a valid activity ID');
    }
    
    return originalId;
  } catch (error) {
    throw new Error(`Failed to decode activity ID: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Generates a public URL with encoded project ID
 * @param projectId - The numeric project ID
 * @param path - The path after the project ID (e.g., 'activities', 'description')
 * @returns The complete public URL with encoded project ID
 */
export function generatePublicUrl(projectId: number, path: string = 'activities'): string {
  const encodedId = encodeProjectId(projectId);
  return `${window.location.origin}/public/project/${encodedId}/${path}`;
} 